// Start loading the editor chunk when somebody is about to open it (pointer over / focus on / touch of the rail's
// "Write an article" link or the composer's "Expand to article" button), so the click finds TipTap already there.
// Main bundle (imported by ui/index.jsx, the three-pane rail and compose_entry.tsx): it must stay tiny and must not
// import the editor, not even dynamically. The /write route's own loader (`() => import('../whocare_article/editor')`
// in ui/index.jsx) registers itself here and the prefetch calls exactly that function: one import site, so the
// chunk's preload list is in the bundle once (a second `import()` in another chunk would repeat it, ~1 KB gz), and
// the browser fetches the chunk once, whoever asks first.

interface NetworkInformation {
  saveData?: boolean;
  effectiveType?: string;
}

let loadEditor: (() => Promise<unknown>) | null = null;
let requested = false;

/** ui/index.jsx, next to the route's async component. */
export const registerArticleEditor = (load: () => Promise<unknown>) => {
  loadEditor = load;
};

/** Data saver on, or a 2G-class connection: wait for the actual click. */
const constrained = () => {
  const connection = (
    navigator as Navigator & { connection?: NetworkInformation }
  ).connection;

  return (
    !!connection &&
    (connection.saveData === true ||
      (connection.effectiveType ?? '').endsWith('2g'))
  );
};

/** At most once per page load; never on load itself, only from the intent listeners below. */
export const prefetchArticleEditor = () => {
  if (requested || !loadEditor || constrained()) {
    return;
  }

  requested = true;

  loadEditor().catch(() => {
    // offline or a deploy replaced the chunk: the route reports it when the editor is actually opened
    requested = false;
  });
};

const INTENT_EVENTS = ['pointerenter', 'focus', 'touchstart'] as const;

/**
 * Callback ref: `<a ref={prefetchOnIntent}>`. The listeners remove themselves after their first event, and
 * prefetchArticleEditor() ignores every call after the first one anyway.
 */
export const prefetchOnIntent = (element: HTMLElement | null) => {
  INTENT_EVENTS.forEach((type) => {
    element?.addEventListener(type, prefetchArticleEditor, {
      once: true,
      passive: true,
    });
  });
};
